import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { AuthResponseDto } from "@reactive-resume/dto";

import { axios } from "@/client/libs/axios";
import { socket } from "@/client/libs/socket";
import { queryClient } from "@/client/libs/query-client";
import { useAuthStore } from "@/client/stores/auth";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PROVISIONING DE COMPTES (console admin) — services client
 * ─────────────────────────────────────────────────────────────────────────────
 *  Import CSV → comptes provisionnés → invitations d'activation.
 *  Temps réel : socket `provisioning:update` (progression des envois).
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type ProvisionBatchStatus = "processing" | "ready" | "inviting" | "done" | "failed";

export type ProvisionBatch = {
  id: string;
  name: string;
  fileName?: string | null;
  source: string;
  status: ProvisionBatchStatus;
  totalRows: number;
  createdCount: number;
  existingCount: number;
  invalidCount: number;
  invitedCount: number;
  claimedCount: number;
  failedCount: number;
  lastError?: string | null;
  createdById?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProvisionRow = {
  userId: string;
  name?: string | null;
  email: string;
  emailVerified: boolean;
  invitationSentAt?: string | null;
  invitationExpiresAt?: string | null;
  invitationAcceptedAt?: string | null;
  invitationSendError?: string | null;
};

export type ProvisionImportResult = {
  batchId: string;
  totalRows: number;
  created: number;
  existing: number;
  invalid: number;
  internalSkipped: number;
  invitationsQueued: number;
  errors: { line: number; email: string | null; reason: string }[];
};

export const PROVISION_KEYS = {
  all: ["provisioning"] as const,
  batches: ["provisioning", "batches"] as const,
  batch: (id: string) => ["provisioning", "batches", id] as const,
};

export const useProvisionBatches = (search?: string) => {
  const query = useQuery({
    queryKey: [...PROVISION_KEYS.batches, search],
    queryFn: async () => {
      const response = await axios.get<{ items: ProvisionBatch[]; nextCursor: string | null }>(
        "/provisioning/batches",
        { params: { search, limit: 50 } },
      );
      return response.data;
    },
  });

  const queryClientRef = useQueryClient();
  useEffect(() => {
    const invalidate = () => queryClientRef.invalidateQueries({ queryKey: PROVISION_KEYS.all });
    socket.on("provisioning:update", invalidate);
    return () => {
      socket.off("provisioning:update", invalidate);
    };
  }, [queryClientRef]);

  return { batches: query.data?.items ?? [], loading: query.isPending };
};

export const useProvisionBatchRows = (
  batchId: string,
  params: { claimed?: "all" | "claimed" | "unclaimed"; search?: string } = {},
) => {
  const query = useQuery({
    queryKey: [...PROVISION_KEYS.batch(batchId), params],
    queryFn: async () => {
      const response = await axios.get<{
        batch: ProvisionBatch;
        items: ProvisionRow[];
        nextCursor: string | null;
      }>(`/provisioning/batches/${batchId}/rows`, { params: { ...params, limit: 100 } });
      return response.data;
    },
    enabled: Boolean(batchId),
  });
  return { batch: query.data?.batch, rows: query.data?.items ?? [], loading: query.isPending };
};

export const useImportProvisionBatch = () => {
  return useMutation({
    mutationFn: async (payload: { file: File; batchName: string; sendInvites: boolean }) => {
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("batchName", payload.batchName);
      formData.append("sendInvites", String(payload.sendInvites));
      const response = await axios.post<ProvisionImportResult>("/provisioning/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROVISION_KEYS.all }),
  });
};

export const useResendInvites = () => {
  return useMutation({
    mutationFn: async ({
      batchId,
      onlyExpired = false,
    }: {
      batchId: string;
      onlyExpired?: boolean;
    }) => {
      const response = await axios.post<{ queued: number }>(
        `/provisioning/batches/${batchId}/resend-invites`,
        {
          onlyUnclaimed: true,
          onlyExpired,
        },
      );
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROVISION_KEYS.all }),
  });
};

export const useDeleteProvisionBatch = () => {
  return useMutation({
    mutationFn: async (batchId: string) => {
      await axios.delete(`/provisioning/batches/${batchId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROVISION_KEYS.all }),
  });
};

/** Télécharge l'export CSV du lot. */
export const exportProvisionBatch = async (batchId: string) => {
  const response = await axios.get<string>(`/provisioning/batches/${batchId}/export`, {
    responseType: "text",
  });
  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `lot-${batchId}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
};

// ── Activation publique (page /auth/activate) ───────────────────────────────

export const validateClaim = async (token: string) => {
  const response = await axios.post<{
    valid: boolean;
    expired?: boolean;
    name?: string | null;
    expiresAt?: string | null;
  }>("/auth/claim/validate", { token });
  return response.data;
};

export const completeClaim = async (token: string, password: string) => {
  // Les cookies de session (auto-login) sont posés par le serveur.
  const response = await axios.post<AuthResponseDto>("/auth/claim/complete", { token, password });
  return response.data;
};

/**
 * Activation du compte + AUTO-LOGIN côté client.
 *
 * Même mécanique que `useRegister` : on injecte l'utilisateur renvoyé par le
 * serveur dans le store d'authentification et dans le cache React Query, puis
 * on redirige vers l'espace utilisateur. Les cookies (access + refresh) sont
 * posés par le serveur dans la réponse de `claim/complete`.
 */
export const useClaimAccount = () => {
  const setUser = useAuthStore((state) => state.setUser);
  const navigate = useNavigate();

  const {
    error,
    isPending: loading,
    mutateAsync: claim,
  } = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      completeClaim(token, password),

    onSuccess: (data) => {
      setUser(data.user);
      queryClient.setQueryData(["user"], data.user);
      void navigate("/client/user-space/resumes");
    },
  });

  return { claim, loading, error };
};
